import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import useStyles from '../../theme/useStyles';
import FamilyDetails from '../families/FamilyDetails';
import MemberManagement from '../members/MemberManagement';
import SacramentStatus from '../members/SacramentStatus';
import PendingRegistrations from '../registration/PendingRegistrations';
import { CongregationSummaryTab } from './CongregationSummary';
import { memberRegistrations, members as membersApi, families as familiesApi } from '../../api/endpoints';
import { useNotification, useOpenRecordFromLink, fetchRecord, RECORD_GONE } from '../../hooks';
import { Notification } from '../../components/ui';
import type { Family, Member } from '../../types';
// Bulk Upload is switched off for now — see the commented tab below.
// import CongregationImport from '../members/CongregationImport';

// Families and Members are one register with two faces, so they live behind one
// tile and a slim switch. Each screen is dropped in whole and unchanged — this
// wrapper only decides which is shown and carries the one hand-off between them:
// "Add member" on a family opens the Members side with that family pre-linked.

type Tab = 'summary' | 'families' | 'members' | 'sacraments' | 'pending'; // | 'import' — Bulk Upload off

const Congregation = ({ isDarkMode }) => {
  const s = useStyles(isDarkMode);
  const t = s.t;

  // Old bookmarks decide the opening face: /members -> Members, /families ->
  // Families; everything else (/congregation) opens on the Summary.
  const { moduleRoute } = useParams();
  const [tab, setTab] = useState<Tab>(
    moduleRoute === 'members' ? 'members' : moduleRoute === 'families' ? 'families' : 'summary'
  );
  const { notification, notify, dismiss } = useNotification();

  // Opened from the Approvals inbox: go straight to that registration — also
  // when already here. `n` remounts the queue so the same one opens again.
  const location = useLocation();
  const navigate = useNavigate();
  const [openReg, setOpenReg] = useState<{ id: number; n: number } | null>(null);
  useEffect(() => {
    const id = (location.state as { openRegistrationId?: number } | null)?.openRegistrationId;
    if (id == null) return;
    navigate(location.pathname, { replace: true, state: null });
    switchTo('pending');
    setOpenReg({ id: Number(id), n: Date.now() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ...or to a member or a family. The request does not say which: the one
  // whose number is the request's reference wins (ids repeat across the two).
  const [openMember, setOpenMember] = useState<Member | null>(null);
  const [openFamily, setOpenFamily] = useState<Family | null>(null);
  useOpenRecordFromLink(async ({ id, ref }) => {
    const [member, family] = await Promise.all([
      fetchRecord(id, undefined, { member: membersApi.get }),
      fetchRecord(id, undefined, { family: familiesApi.get })
    ]);
    // A member's number is its memberId, a family's its familyId; failing a
    // match, a member is the likelier subject of a request.
    const found = (ref && member?.record.memberId === ref && member)
      || (ref && family?.record.familyId === ref && family)
      || member || family;
    if (!found) { notify(RECORD_GONE, 'error'); return; }
    if (found.kind === 'family') {
      switchTo('families');
      setOpenFamily(found.record);
    } else {
      switchTo('members');
      setOpenMember(found.record);
    }
  });

  // Set when a family's "Add member" is used; seeds the Members registration form.
  const [seedFamilyId, setSeedFamilyId] = useState<string | undefined>(undefined);
  // Set when a family's "Edit" is used; opens that family's head in Members, where
  // the head and the family are edited together on one page.
  const [editHeadFamilyId, setEditHeadFamilyId] = useState<string | undefined>(undefined);

  // Families waiting in the public-registration queue, shown on the tab so the
  // office notices them from any of the other faces.
  const [pendingCount, setPendingCount] = useState(0);
  const refreshPending = useCallback(() => {
    memberRegistrations.counts()
      .then((c) => setPendingCount(Number(c?.PENDING) || 0))
      .catch(() => { /* the tab still works without its count */ });
  }, []);
  useEffect(() => { refreshPending(); }, [refreshPending]);

  const goAddMember = (familyId: string) => {
    setEditHeadFamilyId(undefined);
    setSeedFamilyId(familyId);
    setTab('members');
  };

  const goEditFamily = (familyId: string) => {
    setSeedFamilyId(undefined);
    setEditHeadFamilyId(familyId);
    setTab('members');
  };

  const switchTo = (next: Tab) => {
    // These are one-shot hand-offs; leaving the Members tab clears them so a
    // later manual visit starts blank.
    if (next !== 'members') { setSeedFamilyId(undefined); setEditHeadFamilyId(undefined); }
    // So are the Approvals inbox's "Open the record" ones.
    setOpenReg(null); setOpenMember(null); setOpenFamily(null);
    setTab(next);
  };

  const TABS: Array<{ key: Tab; label: string }> = [
    // Figures across both registers; read-only, so it opens first.
    { key: 'summary', label: '📊 Summary' },
    // Named for the action that fills it: a household is created by adding a
    // Head on the Members side, so both places use the same word.
    { key: 'families', label: '👤 Heads' },
    { key: 'members', label: '👥 Members' },
    // Reads the same member records the other two write, so it belongs beside
    // them rather than behind a tile of its own.
    { key: 'sacraments', label: '🕊️ Sacraments' },
    // Families who registered themselves through the public link, waiting for
    // the office to approve them into the two registers above.
    { key: 'pending', label: `📝 Pending Approval${pendingCount ? ` (${pendingCount})` : ''}` }
    // Bulk Upload — fills the same two registers the other tabs edit by hand.
    // Hidden for now; restore this entry, the 'import' key on Tab, the screen's
    // import and the branch below to bring it back.
    // { key: 'import', label: '📄 Bulk Upload' }
  ];

  // The Families / Members switch. It IS the screen's header — passed down and
  // rendered where the old "Family Details" / "Member Management" title sat, so
  // there is no second heading and no wasted row.
  const headerSwitch = (
    <div
      role="tablist"
      aria-label="Congregation view"
     
      style={{
        display: 'inline-flex', gap: '4px', padding: '4px',
        backgroundColor: t.bgSubtle, borderRadius: t.radius.pill, border: `1px solid ${t.border}`
      }}
    >
      {TABS.map(({ key, label }) => {
        const active = tab === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => switchTo(key)}
            style={{
              padding: '8px 20px', cursor: 'pointer', borderRadius: t.radius.pill,
              border: 'none', fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold,
              backgroundColor: active ? t.primary : 'transparent',
              color: active ? '#FFF' : t.textMuted,
              transition: 'background-color 0.15s ease'
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  const notice = <Notification notification={notification} onClose={dismiss} />;

  if (tab === 'summary') {
    return <>{notice}<CongregationSummaryTab isDarkMode={isDarkMode} headerSwitch={headerSwitch} /></>;
  }

  if (tab === 'pending') {
    return (
      <>{notice}<PendingRegistrations
        key={openReg ? `open-${openReg.n}` : 'pending'}
        isDarkMode={isDarkMode} headerSwitch={headerSwitch} onChanged={refreshPending} openId={openReg?.id}
      /></>
    );
  }

  if (tab === 'sacraments') {
    return <>{notice}<SacramentStatus isDarkMode={isDarkMode} headerSwitch={headerSwitch} /></>;
  }

  // if (tab === 'import') {
  //   return <CongregationImport isDarkMode={isDarkMode} headerSwitch={headerSwitch} />;
  // }

  return <>{notice}{tab === 'families'
    ? (
      <FamilyDetails
        isDarkMode={isDarkMode}
        headerSwitch={headerSwitch}
        onAddMember={goAddMember}
        onEditFamily={goEditFamily}
        openFamily={openFamily}
      />
    )
    : (
      // Keyed by the hand-off so switching in with a new family or an edit
      // request remounts the screen onto the right form.
      <MemberManagement
        key={seedFamilyId || editHeadFamilyId || 'members'}
        isDarkMode={isDarkMode}
        headerSwitch={headerSwitch}
        seedFamilyId={seedFamilyId}
        editHeadFamilyId={editHeadFamilyId}
        onPendingChanged={refreshPending}
        onOpenPending={() => switchTo('pending')}
        openMember={openMember}
      />
    )}</>;
};

export default Congregation;
